import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';
import { getNewOperationDate } from 'utils/getNewOperationDate';

import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import { cleanValue } from 'constants/formats';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';

import { splitModalInitValue } from '../constants/splitModalInitValue';

import { checkIsValidNominalValue } from '../utils/checkIsValidNominalValue';

import { SplitModalContextType } from '../types/SplitModalContextType';

const SplitModalContext =
  createContext<SplitModalContextType>(splitModalInitValue);

export const SplitModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const [scriptureView, setScriptureView] = useState<boolean>(true);

  const [date, setDate] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');
  const [newNominalValue, setNewNominalValue] = useState<number>(
    actualSociety?.nominalValue || 0
  );

  const [isValidNominalValue, setIsValidNominalValue] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const onChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const onChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const onChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangeNewNominalValue = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const value = cleanValue(event.target.value);

    const isValidNominalValue = checkIsValidNominalValue({
      currentNominalValue: actualSociety?.nominalValue || 0,
      newNominalValue: value,
    });

    setNewNominalValue(value);
    setIsValidNominalValue(isValidNominalValue);
  };

  const handleSaveSplit = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);
        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: file.name,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const splitData = {
          movements: [],
          transactions: [],
          date: newOperationDate,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          notarialRegistration: {
            notaryName,
            notaryCity,
            protocolNr: notaryProtocol,
          },
          split: {
            nominalValue: actualSociety?.nominalValue || 0,
            newNominalValue,
          },
          operationType: operationTypes.SPLIT,
        };

        await store.dispatch(addOperation(splitData));
        setIsLoading(false);

        successSwal(t('SplitSuccessfullyCompleted'));
      } catch (error) {
        errorSwal(t('ErrorWhenPerformingSplit'));
      } finally {
        handleCloseModal();
      }
    }
  };

  return (
    <SplitModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          lastOperationDate: formattedLastOperationDate,
          nominalValue: actualSociety?.nominalValue || 0,
          newNominalValue,
          societySharesCount: actualSociety?.sharesCount?.actual || 0,
          isValidNominalValue,
          scriptureView,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          onChangeNotaryName,
          onChangeNotaryProtocol,
          onChangeNotaryCity,
          handleChangeNewNominalValue,
          handleSaveSplit,
        },
      }}
    >
      {children}
    </SplitModalContext.Provider>
  );
};

export const useSplitModal = () => {
  const context = useContext(SplitModalContext);
  if (!context) {
    throw new Error('useSplit must be used within a SplitModalProvider');
  }
  return context;
};
